// ./src/components/features/Vehicle/VehicleImage/VehicleImage.tsx
import React, { useState } from "react";
import { Box, Image } from "@mantine/core";

import {
  getCarImageUrl,
  getVehicleType,
  VehicleType,
} from "@/utils/carHelpers";
import { Car } from "@/utils/api/CarService";
import { VehicleTypeIcon } from "../VehicleTypeIcon";

// ============================================
// Types
// ============================================

export type VehicleImageSize = "xs" | "sm" | "md" | "lg" | "xl";

export interface VehicleImageProps {
  /** Car object containing image URL and body type */
  car?: Car | null;

  /** Override image URL */
  imageUrl?: string | null;

  /** Override vehicle type for fallback icon */
  vehicleType?: VehicleType | null;

  /** Body type ID for determining vehicle type */
  bodyTypeId?: string | number | null;

  /** Predefined size */
  size?: VehicleImageSize;

  /** Custom width (overrides size) */
  width?: number | string;

  /** Custom height (overrides size) */
  height?: number | string;

  /** Border radius */
  radius?: string | number;

  /** Alt text for the image */
  alt?: string;

  /** Custom class name */
  className?: string;
}

// ============================================
// Constants
// ============================================

const SIZE_MAP: Record<
  VehicleImageSize,
  { width: number; height: number; iconSize: number }
> = {
  xs: { width: 40, height: 40, iconSize: 20 },
  sm: { width: 56, height: 56, iconSize: 28 },
  md: { width: 80, height: 80, iconSize: 36 },
  lg: { width: 120, height: 120, iconSize: 48 },
  xl: { width: 160, height: 160, iconSize: 64 },
};

const VEHICLE_TYPE_GRADIENTS: Record<VehicleType, string> = {
  car: "linear-gradient(135deg, var(--mantine-color-brand-5) 0%, var(--mantine-color-brand-7) 100%)",
  motorcycle:
    "linear-gradient(135deg, var(--mantine-color-orange-5) 0%, var(--mantine-color-orange-7) 100%)",
  truck:
    "linear-gradient(135deg, var(--mantine-color-blue-5) 0%, var(--mantine-color-blue-7) 100%)",
  other:
    "linear-gradient(135deg, var(--mantine-color-gray-5) 0%, var(--mantine-color-gray-7) 100%)",
};

// ============================================
// Component
// ============================================

/**
 * Displays a vehicle image with fallback to a gradient background with vehicle type icon.
 */
export const VehicleImage: React.FC<VehicleImageProps> = ({
  car,
  imageUrl: imageUrlProp,
  vehicleType: vehicleTypeProp,
  bodyTypeId,
  size = "md",
  width: widthProp,
  height: heightProp,
  radius = "md",
  alt = "Vehicle",
  className,
}) => {
  const [imageError, setImageError] = useState(false);

  // Determine image URL
  const imageUrl = imageUrlProp ?? getCarImageUrl(car);
  const hasImage = Boolean(imageUrl) && !imageError;

  // Determine vehicle type for fallback
  const vehicleType = vehicleTypeProp ?? getVehicleType(car, bodyTypeId);

  // Get dimensions
  const sizeConfig = SIZE_MAP[size];
  const width = widthProp ?? sizeConfig.width;
  const height = heightProp ?? sizeConfig.height;
  const iconSize = sizeConfig.iconSize;

  // Handle image load error
  const handleImageError = () => {
    setImageError(true);
  };

  // Render image if available
  if (hasImage) {
    return (
      <Image
        src={imageUrl!}
        alt={alt}
        w={width}
        h={height}
        radius={radius}
        fit="cover"
        onError={handleImageError}
        className={className}
      />
    );
  }

  // Render fallback with gradient and icon
  return (
    <Box
      w={width}
      h={height}
      className={className}
      style={{
        background: VEHICLE_TYPE_GRADIENTS[vehicleType],
        borderRadius:
          typeof radius === "number"
            ? radius
            : `var(--mantine-radius-${radius})`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "white",
      }}
    >
      <VehicleTypeIcon vehicleType={vehicleType} size={iconSize} bare />
    </Box>
  );
};
